/**
 * REEDOS Knowledge Base — top-level layout for every /kb/* route.
 *
 * Renders:
 *   · Header      — title, breadcrumb, search box
 *   · Sidebar     — role-filtered nav tree (desktop) or a mobile drawer
 *   · Main        — the child route's page content
 *
 * Consumes:
 *   · useKbConfig()       — product name resolution + version categories
 *   · useKbNavigation()   — sidebar content
 *
 * All /kb/* pages inherit this layout via <Outlet />.
 */
import { Outlet, useParams, Link } from "react-router-dom";
import { useState } from "react";
import { Menu, BookOpen, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import KBSidebar, { flattenSidebarSlugs } from "@/pages/kb/KBSidebar";
import KBSearchBox from "@/pages/kb/KBSearchBox";
import { useKbConfig, useKbNavigation } from "@/lib/kb";
import "@/pages/kb/kb.css";

export default function KBLayout() {
    const { slug } = useParams();
    const { data: cfg } = useKbConfig();
    const { data: nav, loading: navLoading, error: navError } = useKbNavigation();
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    const productCodeToName = Object.fromEntries(
        (cfg?.products || []).map((p) => [p.code, p.name]),
    );
    const nodes = nav?.nodes || [];
    const flatSlugs = flattenSidebarSlugs(nodes);

    return (
        <div className="kb-root min-h-screen flex flex-col bg-background">
            {/* ── Header ────────────────────────────────────────── */}
            <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-30 kb-print-hidden">
                <div className="flex items-center gap-3 px-4 py-3 max-w-[1400px] mx-auto">
                    <Link
                        to="/kb"
                        className="flex items-center gap-2 font-semibold text-foreground hover:text-primary shrink-0"
                        data-testid="kb-header-home"
                    >
                        <BookOpen className="h-5 w-5" />
                        <span className="hidden sm:inline">REEDOS Knowledge Base</span>
                        <span className="sm:hidden">KB</span>
                    </Link>
                    <div className="flex-1 max-w-2xl">
                        <KBSearchBox />
                    </div>
                    <div className="flex items-center gap-1">
                        <Link
                            to="/kb/changelog"
                            className="hidden md:flex items-center gap-1 px-2 py-1 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-muted"
                            data-testid="kb-header-changelog"
                        >
                            <History className="h-4 w-4" />
                            Changelog
                        </Link>
                        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
                            <SheetTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="md:hidden kb-sidebar-mobile-trigger"
                                    data-testid="kb-sidebar-mobile-trigger"
                                    aria-label="Open navigation"
                                >
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-80 overflow-y-auto p-0">
                                <SheetHeader className="px-4 py-3 border-b border-border">
                                    <SheetTitle>Navigation</SheetTitle>
                                </SheetHeader>
                                <div onClick={() => setMobileSidebarOpen(false)}>
                                    <KBSidebar
                                        nodes={nodes}
                                        productCodeToName={productCodeToName}
                                        currentSlug={slug}
                                    />
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>

            {/* ── Body: sidebar + main ─────────────────────────── */}
            <div className="flex-1 max-w-[1400px] mx-auto w-full">
                <div className="md:grid md:grid-cols-[280px_1fr] gap-0">
                    <aside
                        className="kb-sidebar-desktop hidden md:block border-r border-border overflow-y-auto sticky top-[57px] max-h-[calc(100vh-57px)] py-3"
                        aria-label="Sidebar"
                    >
                        {navLoading ? (
                            <div className="p-4 text-sm text-muted-foreground">Loading navigation…</div>
                        ) : navError ? (
                            <div className="p-4 text-sm text-destructive">Failed to load navigation.</div>
                        ) : (
                            <KBSidebar
                                nodes={nodes}
                                productCodeToName={productCodeToName}
                                currentSlug={slug}
                            />
                        )}
                    </aside>
                    <main className="kb-main px-4 md:px-8 py-6 min-w-0">
                        <Outlet context={{ nodes, flatSlugs, cfg, productCodeToName }} />
                    </main>
                </div>
            </div>
        </div>
    );
}
